import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import { toTitleCase } from '../../../utils/formHelpers';
import './Classes.css';

const DURATION_OPTIONS = ['1 Year', '2 Years', '3 Years', '4 Years', '5 Years', '6 Years'];
const MEDIUMS = ['Hindi', 'English'];

const Classes = () => {
  const { user }                            = useAuth();
  const { colleges, loading: collegesLoading }                            = useColleges();
  const isScoped                            = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [classesList, setClassesList]       = useState([]);
  const [loading, setLoading]               = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [showSearchDrop, setShowSearchDrop] = useState(false);
  const [filterCollege, setFilterCollege]   = useState('');
  const [showAddForm, setShowAddForm]       = useState(false);
  const [editId, setEditId]                 = useState(null);
  const [expandedRow, setExpandedRow]       = useState(null);

  // Form fields
  const [formName, setFormName]             = useState('');
  const [formDuration, setFormDuration]     = useState('');
  const [formMediums, setFormMediums] = useState(['English']);  // [] | ['Hindi'] | ['English'] | ['Hindi','English']
  const [selectedColleges, setSelectedColleges] = useState([]);

  // College dropdown in form
  const [collegeSearch, setCollegeSearch]   = useState('');
  const [showCollegeDrop, setShowCollegeDrop] = useState(false);
  const collegeDropRef                      = useRef(null);
  const searchRef                           = useRef(null);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/classes');
      setClassesList(data.data || []);
    } catch {
      Swal.fire('Error', 'Failed to load classes.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClasses(); }, []);

  useEffect(() => {
    if (colleges.length === 1)
      setSelectedColleges([colleges[0]._id]);
  }, [colleges]);

  useEffect(() => {
    const handler = (e) => {
      if (collegeDropRef.current && !collegeDropRef.current.contains(e.target)) setShowCollegeDrop(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredCollegeOptions = useMemo(() => {
    if (!collegeSearch) return colleges;
    const q = collegeSearch.toLowerCase();
    return colleges.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [colleges, collegeSearch]);

  const allSelected = selectedColleges.length === colleges.length && colleges.length > 0;

  const toggleCollege = (id) => {
    setSelectedColleges(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedColleges(allSelected ? [] : colleges.map(c => c._id));
  };

  const toggleMedium = (m) => {
    setFormMediums(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };

  const resetForm = () => {
    setFormName('');
    setFormDuration('');
    setFormMediums(['English']);
    setSelectedColleges(colleges.length >= 1 ? [colleges[0]._id] : []);
    setEditId(null);
    setShowAddForm(false);
    setCollegeSearch('');
    setShowCollegeDrop(false);
  };

  const handleSubmit = async () => {
    if (!formName.trim()) { Swal.fire('Missing', 'Please enter class name.', 'warning'); return; }
    if (!formDuration)    { Swal.fire('Missing', 'Please select duration.', 'warning'); return; }
    if (formMediums.length === 0) { Swal.fire('Missing', 'Please select at least one medium.', 'warning'); return; }
    if (selectedColleges.length === 0) { Swal.fire('Missing', 'Please select at least one college.', 'warning'); return; }

    setSaving(true);
    try {
      if (editId) {
        // Edit — update single row (medium is single for existing row)
        const medium = formMediums[0];
        await api.put(`/classes/${editId}`, { name: formName, duration: formDuration, medium });
        Swal.fire('Updated!', 'Class updated.', 'success');
        resetForm();
        loadClasses();
      } else {
        // Create — one row per college × per medium
        const combos = [];
        selectedColleges.forEach(collegeId => {
          formMediums.forEach(medium => {
            combos.push({ name: formName, college: collegeId, duration: formDuration, medium, order: 1 });
          });
        });
        await Promise.all(combos.map(payload => api.post('/classes', payload)));
        Swal.fire('Added!', `Created ${combos.length} class row(s).`, 'success');
        resetForm();
        loadClasses();
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cls) => {
    setEditId(cls._id);
    setFormName(cls.name);
    setFormDuration(cls.duration || '');
    setFormMediums(cls.medium ? [cls.medium] : []);
    setSelectedColleges([cls.college?._id || cls.college || '']);
    setShowAddForm(true);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (cls) => {
    Swal.fire({
      title: `Delete "${cls.name}"?`, text: 'This cannot be undone.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#e74c3c', confirmButtonText: 'Yes, delete it',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await api.delete(`/classes/${cls._id}`);
        setClassesList(prev => prev.filter(c => c._id !== cls._id));
        Swal.fire('Deleted!', 'Class/Course deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
      }
    });
  };

  const handleToggleActive = async (cls) => {
    const isActive = cls.isActive !== false;
    const action   = isActive ? 'Deactivate' : 'Activate';
    const result   = await Swal.fire({
      title: `${action} "${cls.name}"?`,
      text: isActive
        ? 'Deactivated classes will not appear for new student enrollment. Existing students are not affected.'
        : 'This class/course will be available for new student enrollment.',
      icon: 'question', showCancelButton: true,
      confirmButtonColor: isActive ? '#f59e0b' : '#10b981',
      confirmButtonText: `Yes, ${action}`
    });
    if (!result.isConfirmed) return;
    try {
      await api.patch(`/classes/${cls._id}/toggle`);
      setClassesList(prev => prev.map(c => c._id === cls._id ? { ...c, isActive: !isActive } : c));
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed.', 'error');
    }
  };

  const searchSuggestions = useMemo(() => {
    if (!searchQuery) return [];
    const q = searchQuery.toLowerCase();
    return [...new Set(classesList.filter(c => c.name.toLowerCase().includes(q)).map(c => c.name))].slice(0, 8);
  }, [classesList, searchQuery]);

  const displayedClasses = useMemo(() => {
    let result = classesList;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(q) || (c.college?.name || '').toLowerCase().includes(q));
    }
    if (filterCollege) result = result.filter(c => String(c.college?._id || c.college) === filterCollege);
    return result;
  }, [classesList, searchQuery, filterCollege]);

  const selectedCollegeNames = useMemo(() => {
    if (selectedColleges.length === 0) return '-- Select College(s) --';
    if (selectedColleges.length === colleges.length && colleges.length > 0) return 'All Colleges';
    return colleges.filter(c => selectedColleges.includes(c._id)).map(c => c.name).join(', ');
  }, [selectedColleges, colleges]);

  // Preview count
  const previewCount = selectedColleges.length * formMediums.length;

  return (
    <div className="add-classes-program-container">
      <h2 className="add-classes-program-title">Classes / Courses</h2>

      {/* Controls */}
      <div className="add-classes-program-controls">
        {!isScoped && (
          <div className="add-classes-program-search-wrapper" ref={searchRef}>
            <FaSearch className="add-classes-program-search-icon" />
            <input type="text" className="add-classes-program-search-input"
              placeholder="Search by class name..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setShowSearchDrop(true); }}
              onFocus={() => setShowSearchDrop(true)}
            />
            {showSearchDrop && searchSuggestions.length > 0 && (
              <div className="add-classes-program-search-dropdown">
                <ul>
                  {searchSuggestions.map(name => (
                    <li key={name} onClick={() => { setSearchQuery(name); setShowSearchDrop(false); }}>{name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {!isScoped && colleges.length > 1 && (
          <select className="add-classes-program-filter-select" value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <button className="add-classes-program-btn add-classes-program-btn-add"
          onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Class/Course'}
        </button>
      </div>

      {/* Form */}
      {showAddForm && (
        <div className="add-classes-program-inline-form">
          <h3 className="add-classes-program-form-title">{editId ? '✏️ Edit Class/Course' : 'Add New Class/Course'}</h3>
          <div className="add-classes-program-form-grid">

            {/* College */}
            <div className="add-classes-program-form-group">
              <label>College <span className="add-classes-program-req">*</span></label>
              {isScoped || colleges.length === 1 ? (
                <input
                  value={colleges[0]?.name || 'Loading...'}
                  disabled
                  style={{ background: '#f1f5f9', padding: '9px 11px', border: '1px solid #ccc', borderRadius: '6px' }}
                />
              ) : (
                <div className="cls-college-dropdown-wrapper" ref={collegeDropRef}>
                  <div className="cls-college-select-box" onClick={() => setShowCollegeDrop(v => !v)}>
                    <span style={{ color: selectedColleges.length ? '#1e293b' : '#94a3b8', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedCollegeNames}
                    </span>
                    <FaChevronDown style={{ fontSize: '0.75rem', color: '#64748b', flexShrink: 0 }} />
                  </div>
                  {showCollegeDrop && (
                    <div className="cls-college-dropdown">
                      <div className="cls-college-search">
                        <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem' }} />
                        <input type="text" placeholder="Search college..." value={collegeSearch} onChange={e => setCollegeSearch(e.target.value)} autoFocus />
                      </div>
                      {!collegeSearch && !editId && (
                        <div className="cls-college-option cls-select-all" onClick={toggleSelectAll}>
                          <input type="checkbox" checked={allSelected} readOnly />
                          <span>Select All</span>
                        </div>
                      )}
                      <div className="cls-college-options">
                        {filteredCollegeOptions.map(c => (
                          <div key={c._id} className="cls-college-option" onClick={() => editId ? setSelectedColleges([c._id]) : toggleCollege(c._id)}>
                            <input type={editId ? 'radio' : 'checkbox'} checked={selectedColleges.includes(c._id)} readOnly />
                            <span>{c.name}</span>
                            <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 'auto' }}>{c.code}</span>
                          </div>
                        ))}
                        {filteredCollegeOptions.length === 0 && (
                          <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '0.85rem' }}>No colleges found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Class Name */}
            <div className="add-classes-program-form-group">
              <label>Class / Course Name <span className="add-classes-program-req">*</span></label>
              <input type="text" value={formName} onChange={e => setFormName(toTitleCase(e.target.value))} placeholder="e.g. B.Tech, MCA, Class 10" />
            </div>

            {/* Duration */}
            <div className="add-classes-program-form-group">
              <label>Program Duration <span className="add-classes-program-req">*</span></label>
              <select value={formDuration} onChange={e => setFormDuration(e.target.value)}>
                <option value="">-- Select Duration --</option>
                {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {/* Medium */}
            <div className="add-classes-program-form-group">
              <label>Medium <span className="add-classes-program-req">*</span></label>
              <div className="cls-medium-group">
                {MEDIUMS.map(m => (
                  <label key={m} className="cls-medium-option">
                    <input
                      type="checkbox"
                      checked={formMediums.includes(m)}
                      onChange={() => toggleMedium(m)}
                    />
                    <span>{m}</span>
                  </label>
                ))}
              </div>
              {!editId && formMediums.length > 1 && (
                <small style={{ color: '#64748b', fontSize: '0.75rem' }}>Both selected → separate rows per medium</small>
              )}
            </div>
          </div>

          {/* Preview */}
          {!editId && previewCount > 1 && (
            <div style={{ marginBottom: 14, padding: '10px 14px', background: '#eff6ff', borderRadius: 6, fontSize: '0.82rem', color: '#2563eb' }}>
              ℹ️ Will create <strong>{previewCount} separate rows</strong> ({selectedColleges.length} college{selectedColleges.length > 1 ? 's' : ''} × {formMediums.length} medium{formMediums.length > 1 ? 's' : ''})
            </div>
          )}

          <div className="add-classes-program-form-buttons">
            <button className="add-classes-program-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="add-classes-program-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Class/Course' : 'Save Class/Course'}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading classes/courses...</div>
      ) : (
        <div className="add-classes-program-table-wrapper">
          <table className="add-classes-program-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th className="add-classes-program-align-left">Class / Course</th>
                <th className="add-cls-table-duration">Duration</th>
                <th className="add-cls-table-medium">Medium</th>
                <th className="add-cls-table-college">College</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedClasses.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No classes/courses found.</td></tr>
              ) : displayedClasses.map((cls, i) => (
                <React.Fragment key={cls._id}>
                  <tr className="add-classes-program-main-row">
                    <td>{i + 1}</td>
                    <td className="add-classes-program-align-left">
                      <div className="add-classes-program-name-wrapper"
                        onClick={() => setExpandedRow(expandedRow === cls._id ? null : cls._id)}>
                        <span>{cls.name}</span>
                        <button className="add-classes-program-expand-btn">
                          {expandedRow === cls._id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>
                    <td className="add-cls-table-duration">
                      <span className="add-classes-program-badge">{cls.duration || '—'}</span>
                    </td>
                    <td className="add-cls-table-medium">
                      <span className={`add-classes-program-badge ${cls.medium === 'Hindi' ? 'badge-hindi' : cls.medium === 'English' ? 'badge-english' : ''}`}>
                        {cls.medium || '—'}
                      </span>
                    </td>
                    <td className="add-cls-table-college">{cls.college?.name || '—'}</td>
                    <td>
                      <label className="cls-toggle-switch">
                        <input type="checkbox" checked={cls.isActive !== false} onChange={() => handleToggleActive(cls)} />
                        <span className="cls-toggle-slider" />
                      </label>
                      <div style={{fontSize:'0.68rem',marginTop:'3px',color:cls.isActive!==false?'#27ae60':'#e74c3c',fontWeight:700,textAlign:'center'}}>
                        {cls.isActive !== false ? 'Active' : 'Inactive'}
                      </div>
                    </td>
                    <td>
                      <div className="add-classes-program-action-btns">
                        <button className="add-classes-program-btn-edit" onClick={() => handleEdit(cls)} title="Edit"><FaEdit /></button>
                        <button className="add-classes-program-btn-delete" onClick={() => handleDelete(cls)} title="Delete"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                  {expandedRow === cls._id && (
                    <tr className="add-classes-program-expanded-row">
                      <td colSpan="7">
                        <div className="add-classes-program-expanded-content">
                          <div className="add-classes-program-expanded-item add-cls-exp-duration">
                            <span className="add-classes-program-exp-label">Duration</span>
                            <span className="add-classes-program-exp-value">{cls.duration || '—'}</span>
                          </div>
                          <div className="add-classes-program-expanded-item add-cls-exp-medium">
                            <span className="add-classes-program-exp-label">Medium</span>
                            <span className="add-classes-program-exp-value">{cls.medium || '—'}</span>
                          </div>
                          <div className="add-classes-program-expanded-item add-cls-exp-college">
                            <span className="add-classes-program-exp-label">College</span>
                            <span className="add-classes-program-exp-value">{cls.college?.name || '—'}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Classes;
